import { ConfigItem, isConfigGroup } from '../config-items/types';
import { getReadableConfigValues } from '../config-items/utils/helper';
import { FilterAccess, FilterCriteria, FilterCriteriaGroup, FilterPermissions } from '../filters/types';
import { AccessLevel, ShareUser } from '../sharing/types';
import { UserToShare } from './modals';

export const FILTERS_URL = '/preference-filters';

export function mapUserToName(user: UserToShare): string {
  return `${user.givenName || ''} ${user.familyName || ''}`.trim() || user.email;
}

export function getSharedToList(recipients: UserToShare[], lastSeparator = ' and '): string {
  const names = recipients.map(mapUserToName);
  switch (names.length) {
    case 0:
      return '';
    case 1:
      return names[0];
    default:
      return `${names.slice(0, names.length - 1).join(', ')}${lastSeparator}${names[names.length - 1]}`;
  }
}
export const mapToFilterAccessLevel = (access: AccessLevel): FilterAccess => {
  return access === AccessLevel.Owner ? FilterAccess.Owner : FilterAccess.View;
};

export const mapUsersToPermissions = (users: ShareUser[]): FilterPermissions[] => {
  return users.map(user => ({
    access: mapToFilterAccessLevel(user.accessLevel),
    user: {
      id: user.userId,
      email: user.email,
      familyName: user.familyName,
      givenName: user.givenName,
    },
  }));
};

export function createCriteria(item: ConfigItem): FilterCriteria | null {
  if (!item.value) return null;

  const configItemValue = getReadableConfigValues(item.type, [item.value]);
  if (configItemValue.length === 0) return null;

  const name = 'label' in item ? item.label : item.key;
  const value = configItemValue.length === 1 ? configItemValue[0] : configItemValue;

  return { name, value };
}

export function buildFilterCriteria(configItems: ConfigItem[]): FilterCriteria[] {
  const extractCriteria = (items: ConfigItem[]): FilterCriteria[] => {
    return items.flatMap(item => {
      if (isConfigGroup(item) && item.value) {
        return extractCriteria(item.value);
      }
      const criteria = createCriteria(item);
      return criteria ? [criteria] : [];
    });
  };

  return extractCriteria(configItems);
}

export function buildFilterCriteriaGroups(configItems: ConfigItem[]): FilterCriteriaGroup[] {
  const groups: FilterCriteriaGroup[] = [];

  // Handle grouped items
  for (const item of configItems) {
    if (isConfigGroup(item)) {
      const filters = item.value.map(createCriteria).filter((c): c is FilterCriteria => c !== null);
      if (filters.length > 0) {
        groups.push({
          name: item.label || item.key,
          filters,
        });
      }
    }
  }

  // Handle non-grouped items - edge case
  const otherFilters = configItems
    .filter(item => !isConfigGroup(item))
    .map(createCriteria)
    .filter((c): c is FilterCriteria => c !== null);

  if (otherFilters.length > 0) {
    groups.push({
      name: 'Other',
      filters: otherFilters,
    });
  }

  return groups;
}
